/**
 * DV-AUTH-001: Session and Refresh Panel
 *
 * Auth diagnostics panel for session info, refresh controls, and expiry countdown.
 * Displays response headers, trace IDs, user info, and provides structured logging.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Clock, RefreshCw, Shield, User, AlertCircle, CheckCircle, XCircle, Eye, Cookie, Copy } from 'lucide-react';
import { buildApiUrl } from '@/lib/api-base';
import type { LogEntry, LogLevel } from './LogsPanel';
import { CookieEchoPanel, type CookieEchoResult as CookieEchoResultType } from './CookieEchoPanel';
import { useAuthStatusReport } from '@/hooks/useAuthStatusReport';

export interface AuthPanelProps {
  onLog?: (entry: LogEntry) => void;
  className?: string;
  'data-testid'?: string;
}

interface SessionInfo {
  user?: {
    id: string;
    email: string;
    name?: string;
    isAdmin?: boolean;
  };
  isAdmin?: boolean;
  expiresAt?: string | number;
  authenticated?: boolean;
}

interface AuthHeaders {
  traceId?: string;
  reason?: string;
  sessionId?: string;
  refreshed?: string;
}

interface AuthRequest {
  method: string;
  url: string;
  status: number;
  statusText: string;
  headers: AuthHeaders;
  body?: any;
  error?: string;
  timestamp: string;
}

// Use the imported CookieEchoResult type from CookieEchoPanel

export function AuthPanel({ onLog, className = '', 'data-testid': testId = 'auth-panel' }: AuthPanelProps) {
  const [sessionInfo, setSessionInfo] = useState<SessionInfo>({});
  const [lastRequest, setLastRequest] = useState<AuthRequest | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expiryCountdown, setExpiryCountdown] = useState<number | null>(null);
  const [copySuccess, setCopySuccess] = useState(false); // DV-AUTH-004: Copy status feedback

  // DV-AUTH-004: Auth status report functionality
  const { copyToClipboard: copyAuthStatus } = useAuthStatusReport();
  const [showHeaders, setShowHeaders] = useState(false);

  // Cookie echo state
  const [cookieEcho, setCookieEcho] = useState<CookieEchoResultType | null>(null);
  const [echoLoading, setEchoLoading] = useState(false);
  const [echoError, setEchoError] = useState<string | null>(null);
  const [showCookieEcho, setShowCookieEcho] = useState(false);

  // DV-AUTH-003: Simulation controls state
  const [simulateNoCookies, setSimulateNoCookies] = useState(false);
  const [showSimulations, setShowSimulations] = useState(false);

  // AbortController refs for request cancellation
  const abortControllerRef = useRef<AbortController | null>(null);

  // Rate limiting for manual session reload (prevent rapid-fire requests)
  const lastReloadRef = useRef<number>(0);
  const RELOAD_DEBOUNCE_MS = 2000; // 2 seconds between manual reloads

  // Rate limiting for cookie echo test (prevent double clicks)
  const lastEchoRef = useRef<number>(0);
  const ECHO_DEBOUNCE_MS = 500; // 500ms between echo tests

  // Helper to get headers without Content-Type for GET requests (avoids CORS preflight)
  const getAuthHeaders = useCallback((method: 'GET' | 'POST' = 'GET'): HeadersInit => {
    if (method === 'GET') {
      // For GET requests, omit Content-Type to avoid CORS preflight
      return {};
    }
    // For POST requests, include Content-Type
    return {
      'Content-Type': 'application/json',
    };
  }, []);

  // Enhanced logging utility
  const addLog = useCallback((source: string, message: string, level: LogLevel = 'info', extraMeta?: any) => {
    const entry: LogEntry = {
      id: Date.now().toString(),
      ts: new Date().toISOString(),
      level,
      text: `[${source.toUpperCase()}] ${message}`,
      meta: {
        source,
        correlationId: `auth-panel-${Date.now()}`,
        ...extraMeta
      }
    };
    onLog?.(entry);
  }, [onLog]);

  // Parse response headers for auth-specific information
  const parseAuthHeaders = useCallback((headers: Headers): AuthHeaders => {
    const authHeaders: AuthHeaders = {};

    // Extract auth-specific headers
    const traceId = headers.get('X-Auth-Trace-Id') || headers.get('x-auth-trace-id');
    const reason = headers.get('X-Auth-Reason') || headers.get('x-auth-reason');
    const sessionId = headers.get('X-Session-Id') || headers.get('x-session-id');
    const refreshed = headers.get('X-Auth-Refreshed') || headers.get('x-auth-refreshed');

    if (traceId) authHeaders.traceId = traceId;
    if (reason) authHeaders.reason = reason;
    if (sessionId) authHeaders.sessionId = sessionId;
    if (refreshed) authHeaders.refreshed = refreshed;

    return authHeaders;
  }, []);

  // Fetch session information (with simulation support)
  const fetchSession = useCallback(async (options?: { skipCredentials?: boolean }) => {
    // Cancel any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setLoading(true);
    setError(null);

    const timestamp = new Date().toISOString();
    const url = buildApiUrl('/api/auth/session');
    const skipCredentials = options?.skipCredentials || simulateNoCookies;

    if (skipCredentials) {
      addLog('session', `🔬 Simulating no-cookies request to ${url}`, 'warn', {
        endpoint: '/api/auth/session',
        method: 'GET',
        simulation: 'no-cookies'
      });
    } else {
      addLog('session', `Fetching session info from ${url}`, 'info', {
        endpoint: '/api/auth/session',
        method: 'GET'
      });
    }

    try {
      const response = await fetch(url, {
        method: 'GET',
        credentials: skipCredentials ? 'omit' : 'include',
        headers: getAuthHeaders('GET'),
        signal: abortController.signal,
      });

      const authHeaders = parseAuthHeaders(response.headers);
      let responseBody: any = {};

      try {
        responseBody = await response.json();
      } catch (parseError) {
        addLog('session', 'Failed to parse response JSON, treating as empty', 'warn', {
          parseError: String(parseError)
        });
      }

      const requestInfo: AuthRequest = {
        method: 'GET',
        url,
        status: response.status,
        statusText: response.statusText,
        headers: authHeaders,
        body: responseBody,
        timestamp
      };

      setLastRequest(requestInfo);

      if (response.ok) {
        // Transform production API response structure to match frontend expectations
        // Production API returns: { user: {...}, expiresAt: timestamp }
        // Demo API would return: { success: true, data: {...} }
        const transformedSessionInfo: SessionInfo = {
          authenticated: !!responseBody.user, // Production: user presence indicates auth
          user: responseBody.user ? {
            id: responseBody.user.id,
            email: responseBody.user.email,
            name: responseBody.user.name,
            isAdmin: responseBody.user.isAdmin
          } : undefined,
          isAdmin: responseBody.user?.isAdmin || false,
          expiresAt: responseBody.expiresAt ? responseBody.expiresAt : undefined
        };

        setSessionInfo(transformedSessionInfo);
        setError(null);

        addLog('session', `✅ Session fetched successfully`, 'info', {
          status: response.status,
          authenticated: transformedSessionInfo.authenticated,
          isAdmin: transformedSessionInfo.isAdmin,
          userId: transformedSessionInfo.user?.id,
          traceId: authHeaders.traceId,
          reason: authHeaders.reason
        });

        // Set expiry countdown if available
        if (transformedSessionInfo.expiresAt) {
          // Production API returns expiresAt as a timestamp number, not ISO string
          const expiryTime = typeof transformedSessionInfo.expiresAt === 'number'
            ? transformedSessionInfo.expiresAt
            : new Date(transformedSessionInfo.expiresAt).getTime();
          const now = Date.now();
          const timeUntilExpiry = Math.max(0, expiryTime - now);
          setExpiryCountdown(timeUntilExpiry);
        } else {
          // Fallback: assume 55 minutes from now
          const fallbackExpiry = 55 * 60 * 1000; // 55 minutes in milliseconds
          setExpiryCountdown(fallbackExpiry);
          addLog('session', 'No expiresAt provided, using 55min fallback countdown', 'debug');
        }
      } else {
        setError(`HTTP ${response.status}: ${response.statusText}`);

        addLog('session', `❌ Session fetch failed: ${response.status} ${response.statusText}`, 'error', {
          status: response.status,
          statusText: response.statusText,
          traceId: authHeaders.traceId,
          reason: authHeaders.reason,
          responseBody
        });

        if (response.status === 401 || response.status === 403) {
          const isSimulation = skipCredentials;
          const guidanceMessage = isSimulation
            ? '🔬 Simulated no-cookies scenario: This demonstrates what happens when auth cookies are missing'
            : '🔒 Authentication issue detected. Ensure cookies are present; try re-login if needed';

          addLog('session', guidanceMessage, 'warn', {
            guidance: isSimulation ? 'Simulation mode - cookies intentionally omitted' : 'Check browser cookies and authentication state',
            simulation: isSimulation,
            authRequired: true
          });

          if (isSimulation) {
            addLog('session', '💡 Remediation: Enable cookies, check SameSite=None;Secure, verify domain settings', 'info', {
              remediation: true,
              simulation: true
            });
          }
        }
      }

    } catch (networkError) {
      // Check if request was aborted
      if (networkError instanceof Error && networkError.name === 'AbortError') {
        addLog('session', 'Session request cancelled', 'debug', {
          cancelled: true
        });
        return; // Don't update UI state for cancelled requests
      }

      const errorMessage = networkError instanceof Error ? networkError.message : 'Unknown network error';
      setError(errorMessage);

      const requestInfo: AuthRequest = {
        method: 'GET',
        url,
        status: 0,
        statusText: 'Network Error',
        headers: {},
        error: errorMessage,
        timestamp
      };

      setLastRequest(requestInfo);

      addLog('session', `❌ Network error during session fetch: ${errorMessage}`, 'error', {
        error: errorMessage,
        networkFailure: true
      });

    } finally {
      setLoading(false);
    }
  }, [addLog, parseAuthHeaders]);

  // Refresh session (call refresh endpoint then re-fetch session)
  const refreshSession = useCallback(async () => {
    // Cancel any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setLoading(true);
    setError(null);

    const timestamp = new Date().toISOString();
    const refreshUrl = buildApiUrl('/api/auth/refresh');

    addLog('refresh', `Calling refresh endpoint: ${refreshUrl}`, 'info', {
      endpoint: '/api/auth/refresh',
      method: 'POST'
    });

    try {
      const response = await fetch(refreshUrl, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders('POST'),
        signal: abortController.signal,
      });

      const authHeaders = parseAuthHeaders(response.headers);
      let responseBody: any = {};

      try {
        responseBody = await response.json();
      } catch (parseError) {
        addLog('refresh', 'Failed to parse refresh response JSON', 'warn', {
          parseError: String(parseError)
        });
      }

      const requestInfo: AuthRequest = {
        method: 'POST',
        url: refreshUrl,
        status: response.status,
        statusText: response.statusText,
        headers: authHeaders,
        body: responseBody,
        timestamp
      };

      setLastRequest(requestInfo);

      if (response.ok) {
        addLog('refresh', `✅ Refresh successful`, 'info', {
          status: response.status,
          cookiesRotated: authHeaders.refreshed === 'true',
          traceId: authHeaders.traceId,
          reason: authHeaders.reason
        });

        // Re-fetch session to get updated values
        setTimeout(() => {
          fetchSession();
        }, 500); // Small delay to allow cookie updates to propagate

      } else {
        setError(`Refresh failed: HTTP ${response.status}: ${response.statusText}`);

        addLog('refresh', `❌ Refresh failed: ${response.status} ${response.statusText}`, 'error', {
          status: response.status,
          statusText: response.statusText,
          traceId: authHeaders.traceId,
          reason: authHeaders.reason,
          responseBody
        });

        if (response.status === 401 || response.status === 403) {
          addLog('refresh', '🔒 Refresh authentication failed. Session may be expired - re-login required', 'warn', {
            guidance: 'User needs to authenticate again'
          });
        }
      }

    } catch (networkError) {
      // Check if request was aborted
      if (networkError instanceof Error && networkError.name === 'AbortError') {
        addLog('refresh', 'Refresh request cancelled', 'debug', {
          cancelled: true
        });
        return; // Don't update UI state for cancelled requests
      }

      const errorMessage = networkError instanceof Error ? networkError.message : 'Unknown network error';
      setError(`Refresh error: ${errorMessage}`);

      const requestInfo: AuthRequest = {
        method: 'POST',
        url: refreshUrl,
        status: 0,
        statusText: 'Network Error',
        headers: {},
        error: errorMessage,
        timestamp
      };

      setLastRequest(requestInfo);

      addLog('refresh', `❌ Network error during refresh: ${errorMessage}`, 'error', {
        error: errorMessage,
        networkFailure: true
      });

    } finally {
      setLoading(false);
    }
  }, [addLog, parseAuthHeaders, fetchSession]);

  // DV-AUTH-004: Copy auth status report to clipboard
  const handleCopyStatus = useCallback(async () => {
    try {
      const success = await copyAuthStatus();
      if (success) {
        setCopySuccess(true);
        addLog('copy', '📋 Auth status report copied to clipboard', 'info');

        // Clear success indicator after 2 seconds
        setTimeout(() => setCopySuccess(false), 2000);
      } else {
        addLog('copy', '❌ Failed to copy auth status report', 'error');
      }
    } catch (error) {
      addLog('copy', `❌ Copy error: ${error instanceof Error ? error.message : String(error)}`, 'error');
    }
  }, [copyAuthStatus, addLog]);

  // Rate-limited manual session reload
  const reloadSession = useCallback(() => {
    const now = Date.now();
    const timeSinceLastReload = now - lastReloadRef.current;

    if (timeSinceLastReload < RELOAD_DEBOUNCE_MS) {
      const remainingMs = RELOAD_DEBOUNCE_MS - timeSinceLastReload;
      addLog('session', `⏳ Rate limited: wait ${Math.ceil(remainingMs / 1000)}s before next reload`, 'warn', {
        rateLimited: true,
        remainingMs
      });
      return;
    }

    lastReloadRef.current = now;
    addLog('session', 'Manual session reload requested', 'info', {
      manual: true
    });
    fetchSession();
  }, [fetchSession, addLog, RELOAD_DEBOUNCE_MS]);

  // DV-AUTH-003: Force refresh simulation (clears state, then refreshes)
  const forceRefreshSimulation = useCallback(async () => {
    addLog('simulation', '🔬 Force refresh simulation: Clearing session state and testing refresh flow', 'warn', {
      simulation: 'force-refresh',
      action: 'clear-state'
    });

    // Clear current session state to simulate expired scenario
    setSessionInfo({});
    setExpiryCountdown(null);
    setCookieEcho(null);
    setError(null);

    addLog('simulation', '🧪 Session state cleared, attempting refresh...', 'info', {
      simulation: 'force-refresh',
      action: 'attempt-refresh'
    });

    // Attempt refresh after clearing state
    await refreshSession();
  }, [addLog, refreshSession]);

  // Rate-limited cookie echo fetch
  const fetchCookieEcho = useCallback(async () => {
    const now = Date.now();
    const timeSinceLastEcho = now - lastEchoRef.current;

    if (timeSinceLastEcho < ECHO_DEBOUNCE_MS) {
      const remainingMs = ECHO_DEBOUNCE_MS - timeSinceLastEcho;
      addLog('cookie-echo', `⏳ Rate limited: wait ${Math.ceil(remainingMs / 100)}00ms before next test`, 'debug', {
        rateLimited: true,
        remainingMs
      });
      return;
    }

    lastEchoRef.current = now;
    // Cancel any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setEchoLoading(true);
    setEchoError(null);

    const echoUrl = buildApiUrl('/api/auth/_echo');

    addLog('cookie-echo', `Fetching cookie echo from ${echoUrl}`, 'info', {
      endpoint: '/api/auth/_echo',
      method: 'GET'
    });

    try {
      const response = await fetch(echoUrl, {
        method: 'GET',
        credentials: 'include', // Critical for cross-site cookie testing
        headers: getAuthHeaders('GET'),
        signal: abortController.signal,
      });

      const authHeaders = parseAuthHeaders(response.headers);
      let responseBody: CookieEchoResultType = {
        receivedCookies: {},
        hasAt: false,
        hasRt: false,
        timestamp: new Date().toISOString()
      };

      try {
        responseBody = await response.json();
      } catch (parseError) {
        addLog('cookie-echo', 'Failed to parse echo response JSON', 'warn', {
          parseError: String(parseError)
        });
      }

      if (response.ok) {
        setCookieEcho(responseBody);
        setEchoError(null);

        const cookieCount = Object.keys(responseBody.receivedCookies || {}).length;
        const hasRequiredCookies = responseBody.hasAt && responseBody.hasRt;

        addLog('cookie-echo', `✅ Cookie echo successful - ${cookieCount} cookies detected`, 'info', {
          status: response.status,
          cookieCount,
          hasAt: responseBody.hasAt,
          hasRt: responseBody.hasRt,
          origin: responseBody.origin,
          traceId: authHeaders.traceId,
          reason: authHeaders.reason
        });

        if (!hasRequiredCookies) {
          const missingCookies = [];
          if (!responseBody.hasAt) missingCookies.push('access token (hisi_at)');
          if (!responseBody.hasRt) missingCookies.push('refresh token (hisi_rt)');

          addLog('cookie-echo', `⚠️ Missing required cookies: ${missingCookies.join(', ')}`, 'warn', {
            missingCookies,
            possibleCauses: ['blocked third-party cookies', 'SameSite restrictions', 'missing Secure flag', 'cross-origin issues']
          });
        }

        if (responseBody.origin) {
          addLog('cookie-echo', `🌍 Cross-site context detected - Origin: ${responseBody.origin}`, 'debug', {
            origin: responseBody.origin,
            crossSiteTest: true
          });
        }

      } else {
        setEchoError(`HTTP ${response.status}: ${response.statusText}`);

        addLog('cookie-echo', `❌ Cookie echo failed: ${response.status} ${response.statusText}`, 'error', {
          status: response.status,
          statusText: response.statusText,
          traceId: authHeaders.traceId,
          reason: authHeaders.reason,
          responseBody
        });

        if (response.status === 401 || response.status === 403) {
          addLog('cookie-echo', '🔒 Echo endpoint auth failed - check endpoint availability and permissions', 'warn', {
            guidance: 'Verify /api/auth/_echo endpoint exists and is accessible'
          });
        }
      }

    } catch (networkError) {
      // Check if request was aborted
      if (networkError instanceof Error && networkError.name === 'AbortError') {
        addLog('cookie-echo', 'Cookie echo request cancelled', 'debug', {
          cancelled: true
        });
        return; // Don't update UI state for cancelled requests
      }

      const errorMessage = networkError instanceof Error ? networkError.message : 'Unknown network error';
      setEchoError(errorMessage);

      addLog('cookie-echo', `❌ Network error during cookie echo: ${errorMessage}`, 'error', {
        error: errorMessage,
        networkFailure: true
      });

    } finally {
      setEchoLoading(false);
    }
  }, [addLog, parseAuthHeaders, getAuthHeaders, ECHO_DEBOUNCE_MS]);

  // DV-AUTH-003: Detect SameSite/third-party issues from echo results
  const detectSameSiteIssues = useCallback((echo: CookieEchoResultType | null): string[] => {
    const issues: string[] = [];

    if (!echo) return issues;

    const isCrossSite = echo.origin && echo.origin !== window.location.origin;
    const noCookiesReceived = Object.keys(echo.receivedCookies || {}).length === 0;
    const missingAuthCookies = !echo.hasAt || !echo.hasRt;

    if (isCrossSite && noCookiesReceived) {
      issues.push('🚫 Cross-site context: All cookies blocked - likely third-party cookie restrictions');
      issues.push('🍪 Enable "Allow third-party cookies" or add site to exceptions');
    } else if (isCrossSite && missingAuthCookies) {
      issues.push('🔒 Cross-site context: Auth cookies missing - SameSite policy issue');
      issues.push('⚙️ Server needs SameSite=None;Secure for cross-site auth');
    }

    if (echo.origin) {
      issues.push(`🌍 Testing from origin: ${echo.origin}`);
    }

    return issues;
  }, []);

  // Generate remediation hints for missing cookies
  const getCookieRemediationHints = useCallback((missingCookies: string[]): string[] => {
    const hints: string[] = [];

    if (missingCookies.length === 0) return hints;

    hints.push('Missing authentication cookies detected');
    hints.push('Check third-party cookie settings');
    hints.push('Enable "Allow third-party cookies" in browser settings');
    hints.push('Clear browser cookies and re-authenticate if issues persist');

    return hints;
  }, []);

  // Expiry countdown effect
  useEffect(() => {
    if (!expiryCountdown || expiryCountdown <= 0) return;

    const interval = setInterval(() => {
      setExpiryCountdown(prev => {
        if (!prev || prev <= 1000) {
          addLog('session', '⚠️ Session expired based on countdown', 'warn');
          return 0;
        }
        return prev - 1000;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [expiryCountdown, addLog]);

  // Auto-fetch session on mount
  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  // Cleanup on unmount - cancel any pending requests
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Format countdown display
  const formatCountdown = (milliseconds: number): string => {
    if (milliseconds <= 0) return '00:00:00';

    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Get status indicator
  const getStatusIndicator = () => {
    if (loading) return <RefreshCw className="w-4 h-4 animate-spin" style={{ color: 'var(--accent)' }} />;
    if (error) return <XCircle className="w-4 h-4" style={{ color: 'var(--accent-error)' }} />;
    if (sessionInfo.authenticated) return <CheckCircle className="w-4 h-4" style={{ color: 'var(--accent-success)' }} />;
    return <AlertCircle className="w-4 h-4" style={{ color: 'var(--accent-warning)' }} />;
  };

  return (
    <div
      className={`space-y-4 ${className}`}
      data-testid={testId}
      style={{
        backgroundColor: 'var(--bg-surface)',
        border: '1px solid var(--border-default)',
        borderRadius: '8px',
        padding: '16px'
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5" style={{ color: 'var(--accent)' }} />
          <h3 className="text-lg font-semibold" style={{ color: 'var(--fg-primary)' }}>
            Session Diagnostics
          </h3>
          {getStatusIndicator()}
          {cookieEcho?.origin && cookieEcho.origin !== window.location.origin && (
            <div
              className="px-2 py-1 rounded text-xs border"
              style={{
                backgroundColor: 'var(--bg-warning)',
                borderColor: 'var(--border-warning)',
                color: 'var(--fg-warning)',
              }}
              title={`Cross-site context detected: ${cookieEcho.origin}`}
            >
              Cross-Site
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowHeaders(!showHeaders)}
            className="flex items-center gap-1 px-2 py-1 text-xs rounded"
            style={{
              backgroundColor: 'var(--bg-interactive-hover)',
              color: 'var(--fg-muted)'
            }}
            data-testid="auth-panel-toggle-headers"
          >
            <Eye className="w-3 h-3" />
            {showHeaders ? 'Hide' : 'Show'} Headers
          </button>

          <button
            onClick={() => setShowCookieEcho(!showCookieEcho)}
            className="flex items-center gap-1 px-2 py-1 text-xs rounded"
            style={{
              backgroundColor: 'var(--bg-interactive-hover)',
              color: 'var(--fg-muted)'
            }}
            data-testid="auth-panel-toggle-cookies"
          >
            <Cookie className="w-3 h-3" />
            {showCookieEcho ? 'Hide' : 'Show'} Cookies
          </button>

          <button
            onClick={() => setShowSimulations(!showSimulations)}
            className="flex items-center gap-1 px-2 py-1 text-xs rounded"
            style={{
              backgroundColor: showSimulations ? 'var(--bg-warning)' : 'var(--bg-interactive-hover)',
              color: showSimulations ? 'var(--fg-warning)' : 'var(--fg-muted)'
            }}
            data-testid="auth-panel-toggle-simulations"
          >
            <span className="w-3 h-3 text-center">🔬</span>
            {showSimulations ? 'Hide' : 'Show'} Simulations
          </button>

          <button
            onClick={refreshSession}
            disabled={loading}
            className="flex items-center gap-1 px-3 py-1 text-sm rounded border"
            style={{
              backgroundColor: loading ? 'var(--bg-surface)' : 'var(--accent)',
              color: loading ? 'var(--fg-muted)' : 'var(--bg-surface)',
              borderColor: 'var(--border-default)',
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
            data-testid="auth-panel-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* DV-AUTH-003: Simulation Mode Banner */}
      {simulateNoCookies && (
        <div
          className="p-3 rounded border"
          style={{
            backgroundColor: 'var(--bg-warning)',
            borderColor: 'var(--border-warning)',
            color: 'var(--fg-warning)'
          }}
        >
          <div className="flex items-center gap-2">
            <span className="text-lg">🔬</span>
            <span className="text-sm font-medium">Simulation Mode Active</span>
          </div>
          <p className="text-xs mt-1">
            Next session request will omit credentials to simulate no-cookies scenario
          </p>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div
          className="p-3 rounded"
          style={{
            backgroundColor: 'var(--bg-error)',
            border: '1px solid var(--border-error)',
            color: 'var(--fg-error)'
          }}
          data-testid="auth-panel-error"
        >
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm font-medium">Error</span>
          </div>
          <p className="text-sm mt-1">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Session Info */}
        <div
          className="p-4 rounded"
          style={{
            backgroundColor: 'var(--bg-elevated)',
            border: '1px solid var(--border-subtle)'
          }}
          data-testid="auth-panel-session-info"
        >
          <h4 className="text-sm font-medium mb-3 flex items-center gap-2" style={{ color: 'var(--fg-primary)' }}>
            <User className="w-4 h-4" />
            Session Info
          </h4>

          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span style={{ color: 'var(--fg-secondary)' }}>Status:</span>
              <span style={{ color: sessionInfo.authenticated ? 'var(--accent-success)' : 'var(--accent-error)' }}>
                {sessionInfo.authenticated ? '✅ Authenticated' : '❌ Not Authenticated'}
              </span>
            </div>

            {sessionInfo.user && (
              <>
                <div className="flex justify-between">
                  <span style={{ color: 'var(--fg-secondary)' }}>User ID:</span>
                  <span style={{ color: 'var(--fg-primary)' }} className="font-mono text-xs">
                    {sessionInfo.user.id}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span style={{ color: 'var(--fg-secondary)' }}>Email:</span>
                  <span style={{ color: 'var(--fg-primary)' }}>
                    {sessionInfo.user.email}
                  </span>
                </div>

                {sessionInfo.user.name && (
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--fg-secondary)' }}>Name:</span>
                    <span style={{ color: 'var(--fg-primary)' }}>
                      {sessionInfo.user.name}
                    </span>
                  </div>
                )}
              </>
            )}

            <div className="flex justify-between">
              <span style={{ color: 'var(--fg-secondary)' }}>Admin:</span>
              <span style={{ color: (sessionInfo.isAdmin || sessionInfo.user?.isAdmin) ? 'var(--accent-success)' : 'var(--fg-muted)' }}>
                {(sessionInfo.isAdmin || sessionInfo.user?.isAdmin) ? '✅ Yes' : '❌ No'}
              </span>
            </div>
          </div>
        </div>

        {/* Expiry Countdown */}
        <div
          className="p-4 rounded"
          style={{
            backgroundColor: 'var(--bg-elevated)',
            border: '1px solid var(--border-subtle)'
          }}
          data-testid="auth-panel-expiry"
        >
          <h4 className="text-sm font-medium mb-3 flex items-center gap-2" style={{ color: 'var(--fg-primary)' }}>
            <Clock className="w-4 h-4" />
            Session Expiry
          </h4>

          <div className="space-y-2 text-sm">
            {expiryCountdown !== null && (
              <div className="text-center">
                <div
                  className="text-2xl font-mono font-bold"
                  style={{
                    color: expiryCountdown < 60000 ? 'var(--accent-error)' : // < 1 minute
                           expiryCountdown < 300000 ? 'var(--accent-warning)' : // < 5 minutes
                           'var(--accent)'
                  }}
                  data-testid="auth-panel-countdown"
                >
                  {formatCountdown(expiryCountdown)}
                </div>
                <div className="text-xs mt-1" style={{ color: 'var(--fg-secondary)' }}>
                  {sessionInfo.expiresAt ? 'Based on server response' : 'Estimated (55min default)'}
                </div>
              </div>
            )}

            {sessionInfo.expiresAt && (
              <div className="text-xs" style={{ color: 'var(--fg-muted)' }}>
                <div>Expires: {new Date(sessionInfo.expiresAt).toLocaleString()}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Headers Display */}
      {showHeaders && lastRequest && (
        <div
          className="p-4 rounded"
          style={{
            backgroundColor: 'var(--bg-elevated)',
            border: '1px solid var(--border-subtle)'
          }}
          data-testid="auth-panel-headers"
        >
          <h4 className="text-sm font-medium mb-3" style={{ color: 'var(--fg-primary)' }}>
            Last Request Details
          </h4>

          <div className="space-y-2 text-sm font-mono">
            <div className="flex justify-between">
              <span style={{ color: 'var(--fg-secondary)' }}>Method:</span>
              <span style={{ color: 'var(--fg-primary)' }}>{lastRequest.method}</span>
            </div>

            <div className="flex justify-between">
              <span style={{ color: 'var(--fg-secondary)' }}>Status:</span>
              <span style={{
                color: lastRequest.status >= 200 && lastRequest.status < 300 ? 'var(--accent-success)' :
                       lastRequest.status >= 400 ? 'var(--accent-error)' : 'var(--accent-warning)'
              }}>
                {lastRequest.status} {lastRequest.statusText}
              </span>
            </div>

            <div className="flex justify-between">
              <span style={{ color: 'var(--fg-secondary)' }}>Timestamp:</span>
              <span style={{ color: 'var(--fg-primary)' }} className="text-xs">
                {new Date(lastRequest.timestamp).toLocaleString()}
              </span>
            </div>

            {Object.entries(lastRequest.headers).map(([key, value]) => value && (
              <div key={key} className="flex justify-between">
                <span style={{ color: 'var(--fg-secondary)' }}>{key}:</span>
                <span style={{ color: 'var(--accent)' }} className="text-xs break-all">
                  {value}
                </span>
              </div>
            ))}

            {Object.keys(lastRequest.headers).length === 0 && (
              <div className="text-center py-2" style={{ color: 'var(--fg-muted)' }}>
                No auth headers detected
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cookie Echo Panel */}
      {showCookieEcho && (
        <CookieEchoPanel
          echoResult={cookieEcho}
          isLoading={echoLoading}
          error={echoError}
          onRefresh={fetchCookieEcho}
          onGetRemediationHints={getCookieRemediationHints}
        />
      )}

      {/* DV-AUTH-003: Simulation Controls Panel */}
      {showSimulations && (
        <div
          className="rounded-lg border"
          style={{
            backgroundColor: 'var(--bg-elevated)',
            borderColor: 'var(--border-default)',
          }}
        >
          <div
            className="flex items-center justify-between p-3 border-b"
            style={{
              borderColor: 'var(--border-subtle)',
            }}
          >
            <div className="flex items-center space-x-2">
              <span className="text-lg">🔬</span>
              <span
                className="text-sm font-medium"
                style={{ color: 'var(--fg-primary)' }}
              >
                Negative Case Simulations
              </span>
            </div>
          </div>

          <div className="p-3 space-y-3">
            {/* No Cookies Toggle */}
            <div
              className="flex items-center justify-between p-2 rounded border"
              style={{
                backgroundColor: 'var(--bg-surface)',
                borderColor: 'var(--border-subtle)',
              }}
            >
              <div>
                <div
                  className="text-sm font-medium"
                  style={{ color: 'var(--fg-primary)' }}
                >
                  Simulate No Cookies
                </div>
                <div
                  className="text-xs"
                  style={{ color: 'var(--fg-muted)' }}
                >
                  Next session request will omit credentials (401 expected)
                </div>
              </div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={simulateNoCookies}
                  onChange={(e) => setSimulateNoCookies(e.target.checked)}
                  className="mr-2"
                  data-testid="dv-auth-simulate-no-cookies"
                />
                <span
                  className="text-xs"
                  style={{ color: simulateNoCookies ? 'var(--fg-warning)' : 'var(--fg-muted)' }}
                >
                  {simulateNoCookies ? 'Enabled' : 'Disabled'}
                </span>
              </label>
            </div>

            {/* Simulation Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <button
                onClick={() => fetchSession({ skipCredentials: true })}
                disabled={loading}
                className="flex items-center justify-center gap-1 px-3 py-2 text-sm rounded border"
                style={{
                  backgroundColor: loading ? 'var(--bg-surface)' : 'var(--bg-warning)',
                  color: loading ? 'var(--fg-muted)' : 'var(--fg-warning)',
                  borderColor: 'var(--border-warning)',
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
                data-testid="dv-auth-test-no-cookies"
              >
                <span>🚫</span>
                Test No Cookies
              </button>

              <button
                onClick={forceRefreshSimulation}
                disabled={loading}
                className="flex items-center justify-center gap-1 px-3 py-2 text-sm rounded border"
                style={{
                  backgroundColor: loading ? 'var(--bg-surface)' : 'var(--bg-warning)',
                  color: loading ? 'var(--fg-muted)' : 'var(--fg-warning)',
                  borderColor: 'var(--border-warning)',
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
                data-testid="dv-auth-force-refresh"
              >
                <span>🔄</span>
                Force Refresh
              </button>
            </div>

            {/* SameSite Issues Detection */}
            {cookieEcho && (
              <div>
                {detectSameSiteIssues(cookieEcho).length > 0 && (
                  <div
                    className="p-2 rounded border"
                    style={{
                      backgroundColor: 'var(--bg-warning)',
                      borderColor: 'var(--border-warning)',
                    }}
                  >
                    <div className="flex items-start space-x-2">
                      <span className="text-sm">⚠️</span>
                      <div>
                        <div
                          className="text-xs font-medium mb-1"
                          style={{ color: 'var(--fg-warning)' }}
                        >
                          SameSite/Cross-Site Issues Detected
                        </div>
                        <div className="space-y-1">
                          {detectSameSiteIssues(cookieEcho).map((issue, index) => (
                            <div
                              key={index}
                              className="text-xs"
                              style={{ color: 'var(--fg-warning)' }}
                            >
                              {issue}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div
              className="text-xs p-2 rounded"
              style={{
                backgroundColor: 'var(--bg-surface)',
                color: 'var(--fg-muted)',
                borderLeft: '3px solid var(--accent)',
              }}
            >
              💡 Use these simulations to test auth failure scenarios and validate error handling behavior
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="flex items-center justify-between pt-2 border-t" style={{ borderColor: 'var(--border-subtle)' }}>
        <div className="text-xs" style={{ color: 'var(--fg-muted)' }}>
          Endpoint: {buildApiUrl('/api/auth/session')}
        </div>

        <button
          onClick={reloadSession}
          disabled={loading}
          className="text-xs px-2 py-1 rounded"
          style={{
            backgroundColor: 'var(--bg-interactive-hover)',
            color: 'var(--fg-muted)',
            cursor: loading ? 'not-allowed' : 'pointer'
          }}
          data-testid="auth-panel-reload-session"
        >
          Reload Session
        </button>

        <button
          onClick={handleCopyStatus}
          disabled={loading}
          className="text-xs px-2 py-1 rounded flex items-center gap-1"
          style={{
            backgroundColor: copySuccess ? 'var(--success)' : 'var(--bg-interactive-hover)',
            color: copySuccess ? 'var(--bg-primary)' : 'var(--fg-muted)',
            cursor: loading ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.2s ease'
          }}
          data-testid="auth-panel-copy-status"
        >
          <Copy className="w-3 h-3" />
          {copySuccess ? 'Copied!' : 'Copy Status'}
        </button>
      </div>
    </div>
  );
}